import { useLocation } from 'react-router-dom';

import { ComponentType, useEffect } from 'react';

import { SplitFiestaLoading } from '@/components/common/SplitFiestaLoading';

import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useRouter } from '@/hooks/useRouteChangeRouter';

import { useAuthStore } from '@/stores/authStore';

export interface WithAuthConfig {
  redirectTo?: string;
  loadingComponent?: ComponentType;
  /** If true, this page is allowed when offline (default: false) */
  allowOffline?: boolean;
}

export function withAuth<P extends object>(
  Component: ComponentType<P>,
  config?: WithAuthConfig
) {
  const { loadingComponent: LoadingComponent, allowOffline = false } =
    config || {};

  return function ProtectedComponent(props: P) {
    const user = useAuthStore(state => state.user);
    const loading = useAuthStore(state => state.isLoading);
    const hasHydrated = useAuthStore(state => state.hasHydrated);
    const router = useRouter();
    const location = useLocation();
    const { isOnline } = useNetworkStatus();

    useEffect(() => {
      // Only redirect after hydration is complete
      if (hasHydrated && !loading && !user) {
        // Redirect to login without storing intended destination
        // Users will be redirected to home page after successful login
        router.push('/login');
      }
    }, [hasHydrated, loading, user, router]);

    // Redirect to activity when offline (unless this page allows offline or is already activity)
    // This effect handles the actual navigation
    useEffect(() => {
      if (
        hasHydrated &&
        !isOnline &&
        !allowOffline &&
        location.pathname !== '/activity'
      ) {
        router.push('/activity');
      }
    }, [hasHydrated, isOnline, location.pathname, router]);

    // Show loading while hydrating or loading
    if (!hasHydrated || loading) {
      if (LoadingComponent) {
        return <LoadingComponent />;
      }
      return <SplitFiestaLoading />;
    }

    if (!user) {
      return null;
    }

    // CRITICAL: Prevent rendering non-offline-allowed pages while offline
    // This check runs during render (before useEffect) to prevent the component
    // from rendering and triggering queries that would fail offline
    if (!isOnline && !allowOffline && location.pathname !== '/activity') {
      // Show loading state while redirect effect fires
      if (LoadingComponent) {
        return <LoadingComponent />;
      }
      return <SplitFiestaLoading />;
    }

    return <Component {...props} />;
  };
}
